import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress
} from '@mui/material'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { OptionToggle } from '../../../components/_WMS/OptionToggle'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnLocateState, ScanType, eStepAsnLocate } from '../types'
import { getAsnReception } from '../api'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Status } from '../../../hooks/useFetchState'
import { ApiResponse } from '../../../typings/ApiResponse'
import { AsnLocateResult } from '../types'

interface ScanStepProps extends StepProps<AsnLocateState> {}

export const ScanStep: React.FC<ScanStepProps> = ({
  next,
  goToStep,
  sharedState,
  setSharedState
}) => {
  const [scanValue, setScanValue] = useState(sharedState.scanValue)
  const { state, callApi } = useApi()
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  useEffect(() => {
    setScanValue('')
    setSharedState((prev) => ({ ...prev, scanType: 'ASN', scanValue: '', results: [], lpnList: [], lpnAsnId: null, selectedResult: null, selectedLpn: null, hideBackButton: true }))
    return () => {
      setSharedState((prev) => ({ ...prev, hideBackButton: false }))
    }
  }, [])

  const handleScanTypeChange = (newType: ScanType) => {
    setSharedState((prev) => ({ ...prev, scanType: newType, scanValue: '' }))
    setScanValue('')
  }

  const handleScanValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScanValue(e.target.value.trim())
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && scanValue.trim() && !isLoading) handleNext()
  }

  const handleNext = () => {
    const trimmedValue = scanValue.trim()
    if (!trimmedValue) return

    callApi(getAsnReception(sharedState.scanType, trimmedValue))
      .then((response: ApiResponse<AsnLocateResult>) => {
        if (response.totalItems === 0) {
          openSnackBar('No existen ASN con el valor indicado', false)
          return
        }

        const single = response.totalItems === 1 ? response.items[0] : null
        setSharedState((prev) => ({ ...prev, scanValue: trimmedValue, results: response.items, selectedResult: single }))

        if (response.totalItems === 1) {
          goToStep(single!.withLpn ? eStepAsnLocate.LpnStep : eStepAsnLocate.LocateStepV2)
        } else {
          next()
        }
      })
      .catch(() => {
        openSnackBar('Error al buscar el registro', false)
      })
  }

  const handleNoNumber = () => {
    setSharedState((prev) => ({
      ...prev,
      defaultFlowType: sharedState.scanType === 'TN' ? 'Devoluciones' : 'Recepción'
    }))
    goToStep(eStepAsnLocate.UnknownIdStep)
  }

  const isLoading = state.status === Status.Loading

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
        gap: 3,
        height: '100%'
      }}
    >
      <Typography variant='subtitle1' sx={{ color: '#333', fontWeight: 500 }}>
        Selecciona qué quieres escanear
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 500 }}>
        <OptionToggle
          options={['ASN', 'LPN', 'TN']}
          onChange={handleScanTypeChange}
          defaultValue='ASN'
        />
      </Box>

      <TextField
        fullWidth
        variant='outlined'
        placeholder={`Escanea o escribe la ${sharedState.scanType}`}
        value={scanValue}
        onChange={handleScanValueChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: { endAdornment: <ScanIcon width={40} height={52} /> }
        }}
        sx={{
          maxWidth: 500,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px'
          }
        }}
      />

      <Button
        fullWidth
        variant='outlined'
        onClick={handleNoNumber}
        sx={{
          maxWidth: 500,
          borderRadius: '16px',
          color: '#666',
          borderColor: '#bbb',
          textTransform: 'none'
        }}
      >
        No conozco el número de {sharedState.scanType}
      </Button>

      <Button
        fullWidth
        variant='contained'
        color='primary'
        onClick={handleNext}
        disabled={!scanValue.trim() || isLoading}
        sx={{ maxWidth: 500, borderRadius: '10px', py: 1.5, textTransform: 'none' }}
        endIcon={isLoading ? <CircularProgress size='small' /> : undefined}
      >
        Siguiente
      </Button>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Box>
  )
}
